import { useIpStatus } from "../useIpStatus";
import React, {ReactElement, useEffect, useState} from "react";
import {Col, Grid, Row} from "xps-react";
import {RouteWithId} from "../../../routes/types";
import {useHistory, useParams} from "react-router-dom";
import {getTodayDateAsString, toDisplayDateFormat} from "../../../utils/dateUtils";
import {ProfileDetailsProposals} from "./ProfileDetailsProposals";
import useProfileAndProposals from "../../../hooks/useProfileAndProposals";
import {MeetingActions} from "../../Meeting/MeetingActions";
import {Button, DropdownItem} from "../../../components";
import usePageViewTimer from "../../../hooks/usePageViewTimer";
import {ProfileUpdates} from "./ProfileUpdates";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {setHeaderTickerTabs} from "../HeaderTickerTabsSlice";
import {EMPTY_HEADER_TICKER_TABS_RESPONSE} from "../HeaderTickerTabType";
import {resetShowOutOfEstateAssets} from "../../../Assets/common/OutOfEstate/OutOfEstateAssetsSlice";
import {resetAccordionPreferencesState} from "../../../Assets/common/accordionPreferencesSlice";
import {ProfileDetailsActionMenu} from "./ProfileDetailsActionMenu";
import {clientManagementApiClient} from "../../ClientManagementApiClient";
import {
    ProfileAwareActivatePortfolioReserveSwitch
} from "../../../Goals/PortfolioReserve/ActivatePortfolioReserve/ProfileAwareActivatePortfolioReserveSwitch";
import CustomModal from "../../../components/Modal/Custom/CustomModal";
import {LabelPosition} from "../../../Goals/PortfolioReserve/ActivatePortfolioReserve/ActivatePortfolioReserveSwitch";
import {setOldServiceTeam} from "../../../Resources/clientMeetingDocumentSlice";
import {emptyServiceTeam} from "../../../Resources/ResourcesConstant";
import {wealthManagementApiClient} from "../../WealthManagementApiClient";
import {patchProfile, selectProfile} from "../activeProfileSlice";
import {patchApprovedProfile} from "../approvedProfileSlice";
import {patchProposals} from "../../Proposals/proposalsSlice";
import useProfileEditableState from "../../../hooks/useProfileEditableState";
import {useAppInsights} from "src/AppInsights";
import {shortenName} from "../../../Assets/common/textUtils";
import {assetsApiClient} from "../../../Assets/AssetsApiClient";
import {setRestoredProfileInfoState} from "../../PartnerDashboard/ProfileIPInfoSlice";
import {selectPartnerDashboardProfilesSortingInfoState} from "../../PartnerDashboard/ProfileSortingInfoSlice";
import InvalidIPBanner from "../InvalidIPBanner";
import {
    setSampleWasRecentlyDuplicated,
    setShowDuplicateSuccessToast,
} from "../../PartnerDashboard/RecentlyDuplicatedSampleSlice";
import LoadingIndicator from "../../../pages/LoadingIndicator";
import {duplicateNationalSample} from "../../duplicateNationalSample";

interface ProfileTypeInfo {
    readonly profileType: string;
    readonly profileTypePathName: string;
}

interface ActionMenuOptions {
    readonly profileId: string;
    readonly isArchived: boolean;
    readonly isActiveClient: boolean;
    readonly isProspect: boolean;
    readonly isSample: boolean;
    readonly hasProfileIdForProposal: boolean;
    readonly isNationalSampleReadOnly: boolean;
    readonly profileType: string;
    readonly onRestoreProspect: () => void;
    readonly onRestoreSample: () => void;
    readonly onRestoreClient: () => void;
    readonly onConvertToClient: () => void;
    readonly onArchive: () => void;
}

const getProfileTypeInfo = (isProspect: boolean, isSample: boolean, isNationalSample?: boolean): ProfileTypeInfo => {
    if (isProspect) {
        return {
            profileType: "prospect",
            profileTypePathName: "Prospect"
        };
    }

    if (isSample) {
        if (isNationalSample) {
            return {
                profileType: "national sample",
                profileTypePathName: "Sample"
            };
        }

        return {
            profileType: "sample",
            profileTypePathName: "Sample"
        };
    }

    return {
        profileType: "client",
        profileTypePathName: "Client"
    };
};

const formatProfileTypeDisplayName = (profileType: string) => profileType
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const buildActionMenuDropdownItems = ({
    profileId,
    isArchived,
    isActiveClient,
    isProspect,
    isSample,
    hasProfileIdForProposal,
    isNationalSampleReadOnly,
    profileType,
    onRestoreProspect,
    onRestoreSample,
    onRestoreClient,
    onConvertToClient,
    onArchive
}: ActionMenuOptions): ReactElement[] => {
    const profileTypeDisplayName = formatProfileTypeDisplayName(profileType);

    if (isArchived || !isActiveClient) {
        if (isProspect) {
            return [
                <DropdownItem
                    key={`restoreProspect ${profileId}`}
                    aria-label="restoreProspect"
                    onClick={onRestoreProspect}
                    itemText={"Restore Prospect"}
                    value="RestoreProspect"
                />
            ];
        }

        if (isSample) {
            return [
                <DropdownItem
                    key={`restoreSample ${profileId}`}
                    aria-label="restoreSample"
                    onClick={onRestoreSample}
                    itemText={`Restore ${profileTypeDisplayName}`}
                    value="RestoreSample"
                />
            ];
        }

        return [
            <DropdownItem
                key={`restoreClient ${profileId}`}
                aria-label="restoreClient"
                onClick={onRestoreClient}
                itemText={"Restore Client"}
                value="RestoreClient"
                disabled={isArchived && isActiveClient}
            />
        ];
    }

    const dropdownItems: ReactElement[] = [];

    if (isProspect && !hasProfileIdForProposal) {
        dropdownItems.push(
            <DropdownItem
                key={"convertToClient"}
                aria-label={"convertToClient"}
                onClick={onConvertToClient}
                itemText={"Convert to Client"}
                value={"ConvertToClient"}
            />
        );
    }

    if (!isNationalSampleReadOnly) {
        dropdownItems.push(
            <DropdownItem
                key={`archiveClient`}
                aria-label="archiveClient"
                className="viewClientDetails"
                onClick={onArchive}
                itemText={`Archive ${profileTypeDisplayName}`}
                value="ArchiveClient"
            />
        );
    }

    return dropdownItems;
};

const ProfileDetails: React.FC = () => {
    const {id} = useParams<RouteWithId>();
    useIpStatus(id);
    const dispatch = useAppDispatch();
    const history = useHistory();
    const {profile, proposals, isLoading} = useProfileAndProposals(id);
    const profileFromState = useAppSelector(selectProfile)
    const [openArchiveProfileModal, setOpenArchiveProfileModal] = useState(false);
    const [openConvertToClientModal, setOpenConvertToClientModal] = useState(false);
    const {isArchiveReadOnly, isProfileWithProposalsReadOnly, isNationalSampleReadOnly} = useProfileEditableState();
    const [statePRActivationDate, setStatePRActivationDate] = useState<string | null>(null);
    const [isDuplicatingNationalSample, setIsDuplicatingNationalSample] = useState(false);
    const profilesSortingInfo = useAppSelector(selectPartnerDashboardProfilesSortingInfoState)
    usePageViewTimer('Profile Details Page Load Timer (milliseconds)', isLoading);

    useEffect(() => {
        setStatePRActivationDate(profileFromState.portfolioReserveActivationDate ?? getTodayDateAsString());
    }, [profileFromState.portfolioReserveActivationDate]);

    useEffect(() => {
        dispatch(resetAccordionPreferencesState(id));
        dispatch(resetShowOutOfEstateAssets());
        dispatch(setRestoredProfileInfoState({
            ipName: "",
            profileName: null,
            profileOwner: null,
            ipStatus: "",
            restoredProfileName:"",
            wdsGMUrl:""
        }));
    });

    const {profileType, profileTypePathName} = getProfileTypeInfo(profile.prospect, profile.sample, profile.isNationalSample);

    const handleViewProfileNavigation = () => {
        let familyTreePage = `/Profile/${id}/ClientProfile/FamilyTree`;
        let hasActiveProposals = proposals?.filter(p => !p.archived).length > 0;
        if (!isArchiveReadOnly && hasActiveProposals) familyTreePage += "?readOnly=true";
        else if (isArchiveReadOnly) familyTreePage += "?archived=true"
        history.push(familyTreePage);
    };

    const handleProfileSettingsNavigation = () => {
        history.push(`/Profile/${id}/ProfileSettings`);
    };

    const handleNewProposalSettingsNavigation = () => {
        history.push(`/Profile/${id}/NewProposalSettings`);
    };

    const handleViewArchivedProposalsNavigation = () => {
        history.push(`/Profile/${id}/ViewArchivedProposals`);
    };

    const handleArchivingProfileAndDashboardNavigation = async () => {
        setOpenArchiveProfileModal(false)
        await clientManagementApiClient.putProfile({...profile, archived: false, activeClient: false})
            .then(() => {
                assetsApiClient.unlinkInvestmentProgramsForProfile(profile.id);
            })
            .catch(error => console.error('Could not archive profile', error.message));
        history.push(`/Dashboard/${profileTypePathName}`);
    };

    const appins = useAppInsights();
    const handleConvertToClient = () => {
        setOpenConvertToClientModal(false);
        clientManagementApiClient.convertProspect(profile.id).then(() => {
            appins.trackEvent({
                name: 'ProspectConversion',
                properties: {
                    action: 'Prospect Converted',
                    profileId: profile.id,
                    data: 'Prospect converted to client in UI'
                }
            });
            history.push(`/`);
        }).catch((error) => {
            console.error('Could not convert prospect to client', error.message);
        })
    }

    const handleRestoreProfile = (path: string, type: string = "") => {
        clientManagementApiClient.putProfile({
            ...profile,
            archived: false,
            activeClient: true
        }).then(async () => {
            if (type === "Client") {
                await assetsApiClient.restoreInvestmentProgram(profile.id).then(restoreIPResponse => {
                    dispatch(setRestoredProfileInfoState({...restoreIPResponse,restoredProfileName:profile.displayName}));
                }).catch(error => {
                    console.error('Failed to restore investment program', error.message);
                });
            }
            history.push(path);

        });
    }

    const handleProposalSettingsNavigation = (proposalId: string) => {
        history.push(`/Profile/${proposalId}/ProposalSettings/${id}`);
    }

    useEffect(() => {
        dispatch(setHeaderTickerTabs(EMPTY_HEADER_TICKER_TABS_RESPONSE));
        dispatch(setOldServiceTeam(emptyServiceTeam));
        clientManagementApiClient.postProfileSortingInfo(profilesSortingInfo);
    }, [])

    const handleTogglePortfolioReserve = (activePortfolioReserve: boolean) => {
        activePortfolioReserve ? setStatePRActivationDate(getTodayDateAsString()) : setStatePRActivationDate(null)

        wealthManagementApiClient.toggleActivatePortfolioReserve(
            profile.id,
            activePortfolioReserve
        ).then(() => {
            try {
                dispatch(patchProfile({activePortfolioReserve}));
                dispatch(patchApprovedProfile({activePortfolioReserve}));
                dispatch(patchProposals({activePortfolioReserve}));
            } catch (error) {
                console.error('Could not activate portfolio reserve toggle', error);
            }
        })
    };
    const shouldShowDuplicateToMySamplesButton = isNationalSampleReadOnly;

    const handleDuplicateToMySamples = async () => {
        await duplicateNationalSample({
            profileId: profile.id,
            isDuplicatingNationalSample,
            setIsDuplicatingNationalSample,
            onSuccess: () => {
                dispatch(setSampleWasRecentlyDuplicated(true));
                dispatch(setShowDuplicateSuccessToast(true));
                history.push('/Dashboard/Sample');
            },
        });
    };
    const actionMenuDropdownItems = buildActionMenuDropdownItems({
        profileId: profile.id,
        isArchived: profile.archived,
        isActiveClient: profile.activeClient,
        isProspect: profile.prospect,
        isSample: profile.sample,
        hasProfileIdForProposal: profile.profileIdForProposal !== null,
        isNationalSampleReadOnly,
        profileType,
        onRestoreProspect: () => handleRestoreProfile(`/Dashboard/Prospect`),
        onRestoreSample: () => handleRestoreProfile(`/Dashboard/Sample`),
        onRestoreClient: () => handleRestoreProfile(`/`, "Client"),
        onConvertToClient: () => setOpenConvertToClientModal(true),
        onArchive: () => setOpenArchiveProfileModal(true)
    });

    if (isDuplicatingNationalSample) {
        return <LoadingIndicator/>;
    }

    return (
        <>
            <InvalidIPBanner/>
            <CustomModal isOpen={openArchiveProfileModal}
                         title={`Archive this ${profileType}?`}
                         content={`Are you sure you want to archive the selected ${profileType}?`}
                         onClickCancel={() => setOpenArchiveProfileModal(false)}
                         onClickConfirm={() => handleArchivingProfileAndDashboardNavigation()}
                         cancelText='CANCEL'
                         confirmText={`ARCHIVE ${profileType.toUpperCase()}`}
            />

            <CustomModal isOpen={openConvertToClientModal}
                         title={'Convert to Client Profile?'}
                         content={'Are you sure you want to convert this Prospect Profile to a Client Profile? This profile will be moved to the Active Client List on the Partner Dashboard. ' +
                             'To provide access to the Service Team, a PW Client Profile must be linked via Profile Settings.'}
                         onClickCancel={() => setOpenConvertToClientModal(false)}
                         onClickConfirm={() => handleConvertToClient()}
                         cancelText='CANCEL'
                         confirmText={'CONVERT TO CLIENT'}
            />

            <Grid fluid={true}>
                <Row>
                    <Col>
                        <div className="page-container profile-details-container">
                            <div className="profile-details-cta">
                                <div className="h1">{shortenName(profile?.displayName, 50)}</div>
                                <div className="right-aligned display-flex">
                                    {shouldShowDuplicateToMySamplesButton ? (
                                        <Button
                                            id="DuplicateToMySamples"
                                            className="marginleft-sm"
                                            icon="left"
                                            iconName="content_copy"
                                            includeRef={false}
                                            kind="primary"
                                            size="medium"
                                            tabIndex={0}
                                            type="button"
                                            disabled={isDuplicatingNationalSample}
                                            onClick={handleDuplicateToMySamples}
                                        >
                                            <span className="new-client-text">DUPLICATE TO MY SAMPLES</span>
                                        </Button>
                                    ) : (
                                        <>
                                            <ProfileDetailsActionMenu
                                                dropdownItems={actionMenuDropdownItems}
                                            />
                                            <Button
                                                id="NavigateToProfileSettings"
                                                className="marginleft-sm marginright-sm"
                                                icon="none"
                                                iconName="settings"
                                                includeRef={false}
                                                kind="secondary"
                                                size="medium"
                                                tabIndex={0}
                                                type="button"
                                                onClick={handleProfileSettingsNavigation}
                                            >
                                                <span className="new-client-text">PROFILE SETTINGS</span>
                                            </Button>
                                            <Button
                                                id="NavigateToFamilyTree"
                                                className="marginleft-sm"
                                                icon="none"
                                                iconName="settings"
                                                includeRef={false}
                                                kind="primary"
                                                size="medium"
                                                tabIndex={0}
                                                type="button"
                                                onClick={handleViewProfileNavigation}
                                            >
                                                <span
                                                    className="new-client-text">{proposals && proposals.length <= 0 ? 'EDIT PROFILE' : 'VIEW PROFILE'}</span>
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <span className="greyed-out">{
                                `Last updated ${toDisplayDateFormat(profile.lastModifiedDate)} by ${shortenName(profile.lastModifiedByName, 50)}`
                            }</span>
                            {
                                <span className="display-flex">
                                        <ProfileAwareActivatePortfolioReserveSwitch
                                            className="display-flex align-items-center"
                                            labelPosition={LabelPosition.Left}
                                            onTogglePortfolioReserve={handleTogglePortfolioReserve}
                                        />
                                    {profileFromState.activePortfolioReserve &&
                                        <div data-testid="activate-pr-date"
                                             className="marginleft-lg align-self-end color-text--lightgrey">
                                            Activated {toDisplayDateFormat(statePRActivationDate)}
                                        </div>
                                    }
                                    </span>
                            }
                            <ProfileDetailsProposals profileId={profile.id}
                                                     onCreateNewProposal={handleNewProposalSettingsNavigation}
                                                     onUpdateProposal={handleProposalSettingsNavigation}
                                                     onViewArchivedProposals={handleViewArchivedProposalsNavigation}
                                                     proposals={proposals}
                                                     isLoading={isLoading}
                                                     isArchiveReadOnly={isArchiveReadOnly}
                                                     isProfileWithProposalsReadOnly={isProfileWithProposalsReadOnly}
                                                     isNationalSample={profile.isNationalSample}
                            />

                            <section className="">
                                <ProfileUpdates profileId={profile.id}/>
                            </section>
                        </div>
                    </Col>
                    <Col className="meeting-actions-section">
                        <MeetingActions isNationalSample={profile.isNationalSample}/>
                    </Col>
                </Row>
            </Grid>
        </>
    );
};
export default ProfileDetails;